import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import './ForecastModals.css';

// FIXED: Helper function to get NEXT Monday (for Sunday planning)
const getNextMonday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, etc.
  
  let daysAhead;
  if (dayOfWeek === 0) { // Sunday
    daysAhead = 1; // Tomorrow is Monday
  } else if (dayOfWeek === 1) { // Monday
    daysAhead = 7; // Next Monday
  } else { // Tuesday-Saturday
    daysAhead = (8 - dayOfWeek); // Days until next Monday
  }
  
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + daysAhead);
  
  return nextMonday.toISOString().split('T')[0];
};

const SundayPlanningInterface = ({ onClose, onSave }) => {
  const [staff, setStaff] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [forecasts, setForecasts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [staffLoading, setStaffLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false); // NEW: Lock state

  const days = [
    { id: 'Monday', name: 'Monday', short: 'Mon' },
    { id: 'Tuesday', name: 'Tuesday', short: 'Tue' },
    { id: 'Wednesday', name: 'Wednesday', short: 'Wed' },
    { id: 'Thursday', name: 'Thursday', short: 'Thu' },
    { id: 'Friday', name: 'Friday', short: 'Fri' },
    { id: 'Saturday', name: 'Saturday', short: 'Sat' },
    { id: 'Sunday', name: 'Sunday', short: 'Sun' }
  ];

  useEffect(() => {
    console.log('🚀 Component mounted - initializing...');
    const defaultWeek = getNextMonday(); // FIXED: Use getNextMonday
    console.log('📅 Setting default week to:', defaultWeek);
    setSelectedWeek(defaultWeek);
    fetchStaff();
  }, []);

  useEffect(() => {
    if (selectedWeek && staff.length > 0) {
      console.log('🔄 Week or staff changed, loading forecasts for:', selectedWeek);
      loadExistingForecasts(selectedWeek);
    }
  }, [selectedWeek, staff]);

  const fetchStaff = async () => {
    try {
      setStaffLoading(true);
      setError(null);
      
      console.log('👥 Fetching staff members...');
      const response = await api.get('/employees/active');
      
      const employeeData = response.data.data || response.data;
      const revenueStaff = Array.isArray(employeeData) 
        ? employeeData.filter(member => member.generates_revenue !== false)
        : [];
      
      console.log('✅ Staff loaded:', revenueStaff.length, 'revenue-generating members');
      
      if (revenueStaff.length === 0) {
        setError('No active revenue-generating staff found. Please add staff members first.');
        setStaff([]);
        return;
      }
      
      setStaff(revenueStaff);
      initializeEmptyForecasts(revenueStaff);
      
    } catch (error) {
      console.error('❌ Error fetching staff:', error);
      
      if (error.response?.status === 404) {
        setError('Staff endpoint not found. Please check your API configuration.');
      } else if (error.response?.status === 401) {
        setError('Not authorized to access staff data.');
      } else if (error.response?.status === 500) {
        setError('Server error loading staff. Please try again later.');
      } else {
        setError('Failed to load staff list. Please check your connection and try again.');
      }
      
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const initializeEmptyForecasts = (staffList) => {
    console.log('📋 Initializing empty forecasts for', staffList.length, 'staff members');
    const emptyForecasts = {};
    
    staffList.forEach(staffMember => {
      emptyForecasts[staffMember.id] = {};
      days.forEach(day => {
        emptyForecasts[staffMember.id][day.id] = {
          revenue: 0,
          appointments: 0
        };
      });
    });
    
    console.log('✅ Empty forecasts initialized:', emptyForecasts);
    setForecasts(emptyForecasts);
  };

  const loadExistingForecasts = async (weekStart) => {
    try {
      console.log('🔍 Loading existing forecasts for week:', weekStart);
      
      const response = await api.get(`/forecast/weekly?week_start=${weekStart}`);
      
      console.log('📥 API Response:', response.data);
      
      if (response.data && response.data.forecasts && response.data.forecasts.length > 0) {
        const loadedForecasts = {};
        let hasData = false;
        
        // Initialize structure for all staff
        staff.forEach(staffMember => {
          loadedForecasts[staffMember.id] = {};
          days.forEach(day => {
            loadedForecasts[staffMember.id][day.id] = {
              revenue: 0,
              appointments: 0
            };
          });
        });
        
        // Fill in actual data
        response.data.forecasts.forEach(staffData => {
          const staffId = staffData.staff_member_id;
          if (loadedForecasts[staffId]) {
            staffData.forecasts.forEach(forecast => {
              const dayId = forecast.day_of_week;
              if (loadedForecasts[staffId][dayId]) {
                const revenue = forecast.forecasted_revenue || 0;
                const appointments = forecast.forecast_appointments || 0;
                
                loadedForecasts[staffId][dayId] = {
                  revenue,
                  appointments
                };
                
                if (revenue > 0 || appointments > 0) {
                  hasData = true;
                }
              }
            });
          }
        });
        
        console.log('✅ Forecasts loaded and structured:', loadedForecasts);
        setForecasts(loadedForecasts);
        
        // NEW: Lock if data exists (original Sunday forecast was saved)
        setIsLocked(hasData);
        if (hasData) {
          console.log('🔒 Forecast locked - original Sunday data exists');
        }
      } else {
        console.log('ℹ️ No existing forecasts found, using empty structure');
        initializeEmptyForecasts(staff);
        setIsLocked(false);
      }
      
    } catch (error) {
      console.warn('⚠️ Could not load existing forecasts:', error.message);
      initializeEmptyForecasts(staff);
      setIsLocked(false);
    }
  };

  const updateForecast = (staffId, dayId, field, value) => {
    if (isLocked) {
      setError('This forecast is locked. Use "Daily Forecast Update" to make revisions.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    console.log('✏️ Updating forecast:', { staffId, dayId, field, value });
    
    setForecasts(prev => ({
      ...prev,
      [staffId]: {
        ...prev[staffId],
        [dayId]: {
          ...prev[staffId]?.[dayId],
          [field]: value === '' ? 0 : parseFloat(value) || 0
        }
      }
    }));
  };

  const copyPreviousWeek = async () => {
    if (isLocked) {
      setError('This forecast is locked. Use "Daily Forecast Update" to make revisions.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const currentWeekStart = new Date(selectedWeek);
      const previousWeekStart = new Date(currentWeekStart);
      previousWeekStart.setDate(currentWeekStart.getDate() - 7);
      const previousWeekStr = previousWeekStart.toISOString().split('T')[0];
      
      console.log('📋 Copying from previous week:', previousWeekStr);
      
      const response = await api.get(`/forecast/weekly?week_start=${previousWeekStr}`);
      
      if (response.data && response.data.forecasts && response.data.forecasts.length > 0) {
        const copiedForecasts = { ...forecasts };
        
        response.data.forecasts.forEach(staffData => {
          const staffId = staffData.staff_member_id;
          if (copiedForecasts[staffId]) {
            staffData.forecasts.forEach(forecast => {
              const dayId = forecast.day_of_week;
              if (copiedForecasts[staffId][dayId]) {
                copiedForecasts[staffId][dayId] = {
                  revenue: forecast.forecasted_revenue || 0,
                  appointments: forecast.forecast_appointments || 0
                };
              }
            });
          }
        });
        
        console.log('✅ Previous week data copied');
        setForecasts(copiedForecasts);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('No previous week data found to copy.');
        setTimeout(() => setError(null), 3000);
      }
      
    } catch (error) {
      console.error('❌ Error copying previous week:', error);
      setError('Failed to load previous week data.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const saveForecast = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('💾 Starting save process...');
      console.log('Current forecasts state:', forecasts);

      // Prepare data for each staff member
      const savePromises = [];
      
      for (const staffId in forecasts) {
        const staffForecasts = [];
        
        days.forEach(day => {
          const dayData = forecasts[staffId]?.[day.id];
          const revenue = parseFloat(dayData?.revenue) || 0;
          const appointments = parseInt(dayData?.appointments) || 0;
          
          // Only include if there's data
          if (revenue > 0 || appointments > 0) {
            staffForecasts.push({
              day_of_week: day.id,
              forecasted_revenue: revenue,
              forecast_appointments: appointments
            });
          }
        });

        // Save if this staff member has any forecasts
        if (staffForecasts.length > 0) {
          const payload = {
            week_start_date: selectedWeek,
            staff_member_id: staffId,
            forecasts: staffForecasts
          };

          console.log('📤 Saving for staff', staffId, ':', payload);
          savePromises.push(api.post('/forecast/weekly', payload));
        }
      }

      if (savePromises.length === 0) {
        setError('Please enter at least one forecast value before saving.');
        setLoading(false);
        return;
      }

      await Promise.all(savePromises);

      console.log('✅ All forecasts saved successfully - LOCKING forecast');
      setSuccess(true);
      setIsLocked(true); // NEW: Lock after successful save
      
      setTimeout(() => {
        onSave?.();
        onClose?.();
      }, 1500);

    } catch (error) {
      console.error('❌ Error saving forecast:', error);
      
      if (error.response?.status === 400) {
        setError('Invalid forecast data. Please check your entries.');
      } else if (error.response?.status === 409) {
        setError('Forecast already exists for this week. It has been updated.');
        // Still consider it a success and lock
        setIsLocked(true);
        setTimeout(() => {
          onSave?.();
          onClose?.();
        }, 2000);
      } else {
        setError('Failed to save forecast data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTotalForDay = (dayId) => {
    let total = 0;
    Object.keys(forecasts).forEach(staffId => {
      const revenue = parseFloat(forecasts[staffId]?.[dayId]?.revenue) || 0;
      total += revenue;
    });
    return total;
  };

  const getTotalForStaff = (staffId) => {
    let total = 0;
    days.forEach(day => {
      const revenue = parseFloat(forecasts[staffId]?.[day.id]?.revenue) || 0;
      total += revenue;
    });
    return total;
  };

  const getWeekTotal = () => {
    let total = 0;
    Object.keys(forecasts).forEach(staffId => {
      total += getTotalForStaff(staffId);
    });
    return total;
  };

  // NEW: Get count of days with actual forecast data
  const getDaysWithData = () => {
    const daysWithData = new Set();
    Object.keys(forecasts).forEach(staffId => {
      days.forEach(day => {
        const revenue = parseFloat(forecasts[staffId]?.[day.id]?.revenue) || 0;
        if (revenue > 0) {
          daysWithData.add(day.id);
        }
      });
    });
    return daysWithData.size;
  };

  // FIXED: Calculate daily average based on days with data
  const getDailyAverage = () => {
    const total = getWeekTotal();
    const daysCount = getDaysWithData();
    
    if (daysCount === 0) return 0;
    return total / daysCount;
  };

  if (staffLoading) {
    return (
      <div className="forecast-modal-overlay">
        <div className="forecast-modal-container">
          <div className="forecast-modal-header">
            <div className="forecast-modal-title">
              <div className="forecast-modal-icon purple">
                <svg width="24" height="24" fill="none" stroke="#8b5cf6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="forecast-modal-h2">Sunday Planning</h2>
                <p className="forecast-modal-subtitle">Loading your team data...</p>
              </div>
            </div>
            <button onClick={onClose} className="forecast-modal-close">
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="forecast-modal-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <div className="forecast-spinner"></div>
            <span style={{ marginLeft: '12px' }}>Loading staff data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forecast-modal-overlay">
      <div className="forecast-modal-container">
        <div className="forecast-modal-header">
          <div className="forecast-modal-title">
            <div className="forecast-modal-icon purple">
              <svg width="24" height="24" fill="none" stroke="#8b5cf6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="forecast-modal-h2">Sunday Planning {isLocked && '🔒'}</h2>
              <p className="forecast-modal-subtitle">
                {isLocked ? 'Viewing locked forecast - Use Daily Update to revise' : 'Set weekly forecasts for your team'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="forecast-modal-close">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="forecast-modal-content">
          {error && (
            <div className="forecast-alert forecast-alert-error">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="forecast-alert forecast-alert-success">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Forecast data saved successfully!</span>
            </div>
          )}

          {isLocked && (
            <div className="forecast-alert" style={{ backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #3b82f6' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>This forecast is locked to preserve baseline data. Use "Update Tomorrow's Forecast" for daily revisions.</span>
            </div>
          )}

          {staff.length === 0 && !error && (
            <div className="forecast-alert" style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>No active staff members found. Please add staff members to your team first.</span>
            </div>
          )}

          {staff.length > 0 && (
            <>
              <div className="forecast-grid forecast-grid-4" style={{ marginBottom: '24px' }}>
                <div className="forecast-form-group">
                  <label className="forecast-form-label">Week Starting (Monday)</label>
                  <input
                    type="date"
                    value={selectedWeek}
                    onChange={(e) => {
                      if (isLocked) {
                        setError('Cannot change week - forecast is locked.');
                        setTimeout(() => setError(null), 3000);
                        return;
                      }
                      
                      const selectedDate = new Date(e.target.value + 'T00:00:00');
                      const dayOfWeek = selectedDate.getDay();
                      
                      // If not Monday (1), adjust to the nearest Monday
                      if (dayOfWeek !== 1) {
                        const daysToAdjust = dayOfWeek === 0 ? 1 : (1 - dayOfWeek + 7) % 7;
                        selectedDate.setDate(selectedDate.getDate() + daysToAdjust);
                        const adjustedDate = selectedDate.toISOString().split('T')[0];
                        
                        console.log('⚠️ Adjusted non-Monday date to Monday:', adjustedDate);
                        setSelectedWeek(adjustedDate);
                        setError('Week must start on Monday. Date adjusted automatically.');
                        setTimeout(() => setError(null), 3000);
                      } else {
                        console.log('📅 Week changed to:', e.target.value);
                        setSelectedWeek(e.target.value);
                      }
                    }}
                    className="forecast-form-input"
                    disabled={isLocked}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'end' }}>
                  <button
                    onClick={copyPreviousWeek}
                    disabled={loading || isLocked}
                    className="forecast-btn forecast-btn-secondary forecast-btn-full"
                  >
                    Copy Previous Week
                  </button>
                </div>
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'end' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>Week Total</p>
                  <p className="forecast-summary-value purple">£{getWeekTotal().toFixed(2)}</p>
                </div>
              </div>
<hr/>
              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="forecast-planning-table">
                    <thead className="forecast-table-header">
                      <tr>
                        <th>Staff Member</th>
                        {days.map(day => (
                          <th key={day.id}>
                            <div>{day.short}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>£{getTotalForDay(day.id).toFixed(0)}</div>
                          </th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.map(staffMember => (
                        <tr key={staffMember.id} className="forecast-table-row">
                          <td className="forecast-table-cell">
                            <div className="forecast-staff-info">
                              <div className="forecast-staff-avatar">
                                <svg width="16" height="16" fill="#8b5cf6" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div className="forecast-staff-details">
                                <h4>{staffMember.name}</h4>
                                <p>£{getTotalForStaff(staffMember.id).toFixed(2)}</p>
                              </div>
                            </div>
                          </td>
                          {days.map(day => {
                            const dayData = forecasts[staffMember.id]?.[day.id];
                            return (
                              <td key={day.id} className="forecast-table-cell">
                                <div className="forecast-input-group">
                                  <input
                                    type="number"
                                    placeholder="£"
                                    value={dayData?.revenue || ''}
                                    onChange={(e) => updateForecast(
                                      staffMember.id, 
                                      day.id, 
                                      'revenue', 
                                      e.target.value
                                    )}
                                    className="forecast-input-small"
                                    min="0"
                                    step="0.01"
                                    disabled={isLocked}
                                  />
                                  <input
                                    type="number"
                                    placeholder="Appts"
                                    value={dayData?.appointments || ''}
                                    onChange={(e) => updateForecast(
                                      staffMember.id, 
                                      day.id, 
                                      'appointments', 
                                      e.target.value
                                    )}
                                    className="forecast-input-small"
                                    min="0"
                                    disabled={isLocked}
                                  />
                                </div>
                              </td>
                            );
                          })}
                          <td className="forecast-table-cell">
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              padding: '2px 10px', 
                              borderRadius: '9999px', 
                              fontSize: '12px', 
                              fontWeight: '500', 
                              backgroundColor: '#f3e8ff', 
                              color: '#8b5cf6' 
                            }}>
                              £{getTotalForStaff(staffMember.id).toFixed(0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
<hr/>
              <div className="forecast-grid forecast-grid-3" style={{ marginBottom: '24px' }}>
                <div className="forecast-summary-card purple">
                  <div className="forecast-summary-value purple">£{getWeekTotal().toFixed(2)}</div>
                  <div className="forecast-summary-label">Total Week Forecast</div>
                </div>
                <div className="forecast-summary-card green">
                  <div className="forecast-summary-value green">
                    {Object.keys(forecasts).reduce((count, staffId) => {
                      return count + days.filter(day => {
                        const appointments = parseFloat(forecasts[staffId]?.[day.id]?.appointments) || 0;
                        return appointments > 0;
                      }).length;
                    }, 0)}
                  </div>
                  <div className="forecast-summary-label">Scheduled Sessions</div>
                </div>
                <div className="forecast-summary-card blue">
                  <div className="forecast-summary-value blue">£{getDailyAverage().toFixed(0)}</div>
                  <div className="forecast-summary-label">
                    Daily Average {getDaysWithData() > 0 && `(${getDaysWithData()} days)`}
                  </div>
                </div>
              </div>
<hr/>
              <div className="forecast-tips">
                <h3>Planning Tips</h3>
                <ul>
                  <li>Consider historical data and booking patterns</li>
                  <li>Account for holidays, events, and seasonal variations</li>
                  <li>Set realistic but motivating targets for each team member</li>
                  <li>Review and adjust based on actual performance</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="forecast-modal-footer">
          <button
            onClick={onClose}
            disabled={loading}
            className="forecast-btn forecast-btn-secondary"
          >
            {isLocked ? 'Close' : 'Cancel'}
          </button>
          {staff.length > 0 && !isLocked && (
            <button
              onClick={saveForecast}
              disabled={loading || getWeekTotal() === 0}
              className="forecast-btn forecast-btn-primary"
            >
              {loading ? (
                <>
                  <div className="forecast-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Weekly Forecast
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SundayPlanningInterface;